import { useEffect, useState } from "react";

import { Toast } from "@smart/bridge-intake-components-dom";
import { TField } from "@smart/bridge-resources-basic";
import { defaultClientRoleId } from "@smart/bridge-smokeball-basic";
import { buildMessageTemplate } from "@smart/bridge-templates-basic";
import {
  AnalyticsTool,
  isDigitalSignatureField,
  UserNS,
} from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  Alert,
  FieldList,
  Modal,
  ProgressCircle,
  Select,
} from "@smart/itops-sb-design-system-dom";
import { buildDisplayName } from "@smart/itops-utils-basic";
import {
  useLazyLoadFields,
  useLazyLoadMatterTypes,
} from "@smart/manage-gql-client-dom";

import { AnalyticsConfig } from "./analytics-config";
import { EmbedOptions } from "./embed-options";
import { GenerateForm, Staff } from "./types";
import { findClientRoles, validateForm } from "../../shared/validation-utils";

type ValidationStatus = "valid" | "invalid" | "validating";

export type CreateLinkProps = {
  loading?: boolean;
  areasOfLaw: string[];
  locations: string[];
  teamForms: GenerateForm[];
  teamName: string;
  globalForms: GenerateForm[];
  productName: string;
  staff: Staff[];
  user?: Staff;
  onGenerate: (fields: {
    formUri: string;
    slugUri: string;
    notifiedUserUris: string[];
    analyticsTools?: AnalyticsTool[];
  }) => Promise<string | undefined>;
  onCopy: (options: { code: string; toast: Toast }) => Promise<void>;
  loadFields: ReturnType<typeof useLazyLoadFields>;
  loadMatterTypes: ReturnType<typeof useLazyLoadMatterTypes>[0];
  creatingModal: [boolean, (v: boolean) => void];
};

export const CreateLink = ({
  loading,
  areasOfLaw,
  locations,
  teamForms,
  teamName,
  globalForms,
  productName,
  user,
  staff,
  onGenerate,
  onCopy,
  loadFields,
  loadMatterTypes,
  creatingModal,
}: CreateLinkProps) => {
  const [, setModalOpen] = creatingModal;
  const [selectedForm, setSelectedForm] = useState<GenerateForm | undefined>();
  const [validationStatus, setValidationStatus] = useState<
    ValidationStatus | undefined
  >();
  const [hasDigitalSignatureField, setHasDigitalSignatureField] =
    useState(false);
  const [selectedStaff, setSelectedStaff] = useState(user ? [user] : []);
  const [analyticsTools, setAnalyticsTools] = useState<
    AnalyticsTool[] | undefined
  >(undefined);
  const [errorMessage, setErrorMessage] = useState<string>();
  const locale = loadLocale();

  const filterForms = (forms: GenerateForm[]) =>
    forms.filter((form) =>
      form.values.matterTypes.some(
        (mt) =>
          areasOfLaw.includes(mt.category) && locations.includes(mt.location),
      ),
    );

  useEffect(() => {
    if (!selectedForm) return;

    const validate = async (form: GenerateForm) => {
      setValidationStatus("validating");
      const fields = (await loadFields({ formUri: form.uri }))?.map(
        (f) => f.values as unknown as TField,
      );

      setHasDigitalSignatureField(
        !!fields?.some((f) => isDigitalSignatureField(f)),
      );

      if (!fields?.length) return setValidationStatus("invalid");

      if (validateForm(fields, defaultClientRoleId))
        return setValidationStatus("valid");

      const matterTypes = await loadMatterTypes({
        variables: {
          type: form.values.category,
          matterTypeIds: form.values.matterTypes.map((mt) => mt.id),
        },
      });
      const clientRoles = findClientRoles(matterTypes.data?.matterTypes);

      if (clientRoles.some((role) => validateForm(fields, role)))
        return setValidationStatus("valid");

      return setValidationStatus("invalid");
    };

    validate(selectedForm).catch(console.error);
  }, [selectedForm]);

  useEffect(() => {
    setSelectedStaff(user ? [user] : []);
  }, [user]);

  const onCloseModal = () => {
    setErrorMessage(undefined);
    setSelectedStaff([]);
    setAnalyticsTools(undefined);
    setSelectedForm(undefined);
    setValidationStatus(undefined);
    setHasDigitalSignatureField(false);
    setModalOpen(false);
  };

  const getOptions = () => {
    const teamFormsOptions = filterForms(teamForms).map((tf) => ({
      label: tf.values.title,
      value: tf,
    }));

    const globalFormsOptions = filterForms(globalForms).map((gf) => ({
      label: gf.values.title,
      value: gf,
    }));

    const options = [];

    if (teamFormsOptions.length > 0) {
      options.push({
        heading: `${teamName} Forms`,
        items: teamFormsOptions,
      });
    }

    if (globalFormsOptions.length > 0) {
      options.push({
        heading: `${productName} Forms`,
        items: globalFormsOptions,
      });
    }

    return options;
  };

  const canGenerateLink =
    validationStatus === "valid" && !hasDigitalSignatureField;

  return (
    <Modal
      header={{
        text: "Create New Link",
        icon: "solidLink",
        iconBgColor: "bg-cyan-140",
      }}
      loading={loading}
      open={creatingModal[0]}
      onClose={onCloseModal}
      footer={{
        buttons: [
          {
            variant: "secondarySubtle",
            text: "Cancel",
            onClick: onCloseModal,
          },
        ],
      }}
    >
      <div className="flex gap-8 flex-col">
        <p>{`Create a new web ${locale.terms.enquiry.name} link to a form`}</p>
        <FieldList>
          <Select
            name="form"
            label="Form"
            options={getOptions()}
            readOnly={loading}
            value={selectedForm}
            onChange={setSelectedForm}
            dataTestId="select-form"
            empty="No forms available"
          />
          {canGenerateLink && (
            <Select
              id="staff"
              label="Staff to notify upon form completion"
              options={staff.map((s) => ({
                label: buildDisplayName(s, s.userId),
                value: s,
              }))}
              disabled={loading}
              error={!!errorMessage}
              message={errorMessage}
              value={selectedStaff}
              onChange={(vals) => {
                let newValues;
                if (Array.isArray(vals)) newValues = vals;
                else if (vals) newValues = [vals];
                setSelectedStaff(newValues as Staff[]);
              }}
              valueComparer={(currentValue, valueToCompare) =>
                (currentValue as Staff).userId ===
                (valueToCompare as Staff).userId
              }
              dataTestId="tags-staff"
              isMultiple
              empty="No Staff Available"
            />
          )}
          {canGenerateLink && (
            <AnalyticsConfig
              config={analyticsTools}
              setConfig={setAnalyticsTools}
            />
          )}
        </FieldList>
        {hasDigitalSignatureField && (
          <Alert
            variant="section"
            palette="warning"
            content={`The selected form currently has a digital signature field. Please change the signature field to an electronic signature before creating a web ${locale.terms.enquiry.name} link.`}
          />
        )}
        {validationStatus === "validating" && <ProgressCircle />}
        {validationStatus === "invalid" && (
          <Alert
            variant="section"
            palette="warning"
            content="A Web Enquiry form can not be shared without a required mapped field for the Prospective Client Name. Please map this field and mark it as “Required” to continue."
          />
        )}
        {canGenerateLink && (
          <EmbedOptions
            disabled={loading}
            onCopy={async (type) => {
              if (!selectedStaff.length) {
                setErrorMessage("Please select a staff");
                return;
              }
              if (selectedForm) {
                const link = await onGenerate({
                  formUri: selectedForm.uri,
                  slugUri: selectedForm.slug?.uri || "",
                  notifiedUserUris: selectedStaff.map((s) =>
                    UserNS.generateUri("sb", s.userId),
                  ),
                  analyticsTools,
                });

                if (link) {
                  const code =
                    type === "url"
                      ? link
                      : buildMessageTemplate({
                          link,
                          format: "embed",
                          category: "lead",
                          matterType: { name: "" },
                          form: { title: selectedForm.values.title },
                          embedOption: type,
                        }).html;
                  await onCopy({
                    code,
                    toast: {
                      text: `${
                        type === "url" ? "Link" : "Code"
                      } copied and ready to be used`,
                    },
                  });
                }
              }
              onCloseModal();
            }}
          />
        )}
      </div>
    </Modal>
  );
};
